import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  autoOrganizePhotos,
  uploadPhotosToAlbum,
  type PhotoUploadProgress,
  type PhotoUploadResult,
} from '@/services/photoService';

type UploadKind = 'photo' | 'video';
type UploadMode = 'date' | 'album';
type UploadJobStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface QueueUploadFile {
  file: File;
  kind: UploadKind;
}

export interface QueueUploadOptions {
  mode: UploadMode;
  albumId?: string;
}

interface UploadQueueJob {
  id: string;
  batchId: string;
  file: File;
  kind: UploadKind;
  mode: UploadMode;
  albumId?: string;
  status: UploadJobStatus;
  progress: PhotoUploadProgress;
  result?: PhotoUploadResult;
  error?: string;
}

interface CompletedBatchSummary {
  batchId: string;
  totalCount: number;
  uploadedCount: number;
  duplicateCount: number;
  failedCount: number;
  albumIds: string[];
  errorMessages: string[];
}

interface UploadQueueContextValue {
  enqueueUpload: (files: QueueUploadFile[], options: QueueUploadOptions) => string;
  queuedCount: number;
  runningCount: number;
  hasActiveUploads: boolean;
}

const UploadQueueContext = createContext<UploadQueueContextValue | null>(null);

const PHOTO_CONCURRENCY = 2;
const VIDEO_CONCURRENCY = 1;

function makeId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function initialProgress(file: File): PhotoUploadProgress {
  return {
    loaded: 0,
    total: Math.max(1, file.size),
    percent: 0,
    phase: 'uploading',
  };
}

function isTerminal(job: UploadQueueJob): boolean {
  return job.status === 'completed' || job.status === 'failed';
}

function batchSummary(batchId: string, jobs: UploadQueueJob[]): CompletedBatchSummary {
  const batchJobs = jobs.filter((job) => job.batchId === batchId);
  const albumIds = Array.from(new Set(batchJobs.flatMap((job) => job.result?.albumIds ?? [])));
  return {
    batchId,
    totalCount: batchJobs.length,
    uploadedCount: batchJobs.reduce((sum, job) => sum + (job.result?.photos.length ?? 0), 0),
    duplicateCount: batchJobs.reduce((sum, job) => sum + (job.result?.duplicateCount ?? 0), 0),
    failedCount: batchJobs.filter((job) => job.status === 'failed').length,
    albumIds,
    errorMessages: Array.from(new Set(batchJobs.map((job) => job.error).filter(Boolean) as string[])),
  };
}

function QueueOverlay({
  jobs,
  completion,
  onDismissCompletion,
}: {
  jobs: UploadQueueJob[];
  completion: CompletedBatchSummary | null;
  onDismissCompletion: () => void;
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const activeJobs = jobs.filter((job) => !isTerminal(job));
  const finishedJobs = jobs.filter((job) => isTerminal(job));
  const totalBytes = jobs.reduce((sum, job) => sum + Math.max(1, job.file.size), 0);
  const loadedBytes = jobs.reduce((sum, job) => {
    if (isTerminal(job)) return sum + Math.max(1, job.file.size);
    return sum + Math.min(job.progress.loaded, Math.max(1, job.file.size));
  }, 0);
  const percent = totalBytes > 0 ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100)) : 0;
  const queued = activeJobs.filter((job) => job.status === 'queued').length;

  return (
    <>
      {activeJobs.length > 0 && (
        <aside className="fixed left-1/2 top-3 z-[70] w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2 rounded-2xl border border-background-200 bg-background-50/95 p-4 shadow-xl backdrop-blur">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="w-full text-left"
            aria-expanded={expanded}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-foreground-950">
                  업로드 작업 중 · {finishedJobs.length}/{jobs.length} 완료
                </p>
                <p className="mt-0.5 text-[11px] text-foreground-500">
                  사진 최대 2장 또는 동영상 1개씩 처리 · 대기 {queued}개
                </p>
              </div>
              <span className="shrink-0 text-sm font-bold text-accent-600">{percent}%</span>
            </div>
            <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-secondary-100">
              <div className="h-full rounded-full bg-accent-500 transition-[width] duration-300" style={{ width: `${percent}%` }} />
            </div>
          </button>

          {expanded && (
            <div className="mt-3 max-h-52 space-y-2 overflow-y-auto border-t border-background-100 pt-3">
              {jobs.map((job) => (
                <div key={job.id} className="flex items-center gap-3 rounded-xl bg-secondary-50 px-3 py-2">
                  <i className={`${job.kind === 'video' ? 'ri-video-line' : 'ri-image-line'} text-accent-500`} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-foreground-800">{job.file.name}</p>
                    <p className="mt-0.5 text-[10px] text-foreground-400">
                      {job.status === 'queued' && '대기 중'}
                      {job.status === 'running' && (job.progress.phase === 'processing' ? '서버 처리 중' : `전송 ${job.progress.percent}%`)}
                      {job.status === 'completed' && ((job.result?.duplicateCount ?? 0) > 0 ? '중복으로 제외됨' : '완료')}
                      {job.status === 'failed' && `실패 · ${job.error ?? '오류'}`}
                    </p>
                  </div>
                  <span className="text-[10px] font-semibold text-foreground-500">
                    {job.status === 'running' ? `${job.progress.percent}%` : ''}
                  </span>
                </div>
              ))}
              <p className="px-1 text-[10px] leading-4 text-foreground-400">
                앱 안의 다른 메뉴는 자유롭게 사용할 수 있습니다. 브라우저 새로고침이나 앱 종료는 업로드가 끝난 뒤 해주세요.
              </p>
            </div>
          )}
        </aside>
      )}

      {completion && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-foreground-950/35 p-5 backdrop-blur-[1px]">
          <section role="dialog" aria-modal="true" aria-labelledby="upload-complete-title" className="w-full max-w-sm rounded-3xl bg-background-50 p-6 shadow-2xl">
            <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${completion.failedCount > 0 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
              <i className={`${completion.failedCount > 0 ? 'ri-error-warning-line' : 'ri-check-line'} text-2xl`} />
            </div>
            <h2 id="upload-complete-title" className="mt-4 text-center text-xl font-bold text-foreground-950">
              {completion.failedCount > 0 ? '업로드 작업이 끝났어요' : '업로드가 완료됐어요'}
            </h2>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-secondary-50 px-2 py-3">
                <p className="text-lg font-bold text-foreground-900">{completion.uploadedCount}</p>
                <p className="text-[10px] text-foreground-500">저장</p>
              </div>
              <div className="rounded-xl bg-secondary-50 px-2 py-3">
                <p className="text-lg font-bold text-foreground-900">{completion.duplicateCount}</p>
                <p className="text-[10px] text-foreground-500">중복 제외</p>
              </div>
              <div className="rounded-xl bg-secondary-50 px-2 py-3">
                <p className="text-lg font-bold text-foreground-900">{completion.failedCount}</p>
                <p className="text-[10px] text-foreground-500">실패</p>
              </div>
            </div>
            {completion.errorMessages.length > 0 && (
              <div className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs leading-5 text-red-600">
                {completion.errorMessages.slice(0, 2).join(' · ')}
              </div>
            )}
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button type="button" onClick={onDismissCompletion} className="rounded-xl border border-background-200 px-4 py-3 text-sm font-semibold text-foreground-700">
                확인
              </button>
              <button
                type="button"
                onClick={() => {
                  onDismissCompletion();
                  navigate('/albums');
                }}
                className="rounded-xl bg-accent-500 px-4 py-3 text-sm font-semibold text-background-50"
              >
                앨범 목록 보기
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export function UploadQueueProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<UploadQueueJob[]>([]);
  const [completionQueue, setCompletionQueue] = useState<CompletedBatchSummary[]>([]);
  const runningIds = useRef(new Set<string>());
  const notifiedBatchIds = useRef(new Set<string>());

  const updateJob = useCallback((jobId: string, updater: (job: UploadQueueJob) => UploadQueueJob) => {
    setJobs((current) => current.map((job) => (job.id === jobId ? updater(job) : job)));
  }, []);

  const runJob = useCallback(async (job: UploadQueueJob) => {
    if (runningIds.current.has(job.id)) return;
    runningIds.current.add(job.id);
    updateJob(job.id, (current) => ({ ...current, status: 'running' }));

    try {
      const onProgress = (progress: PhotoUploadProgress) => {
        updateJob(job.id, (current) => ({ ...current, progress }));
      };
      const result = job.mode === 'date'
        ? await autoOrganizePhotos([job.file], onProgress)
        : await uploadPhotosToAlbum(job.albumId ?? '', [job.file], onProgress);
      updateJob(job.id, (current) => ({
        ...current,
        status: 'completed',
        result,
        progress: {
          loaded: Math.max(1, current.file.size),
          total: Math.max(1, current.file.size),
          percent: 100,
          phase: 'processing',
        },
      }));
    } catch (error) {
      updateJob(job.id, (current) => ({
        ...current,
        status: 'failed',
        error: error instanceof Error ? error.message : '업로드에 실패했습니다.',
      }));
    } finally {
      runningIds.current.delete(job.id);
    }
  }, [updateJob]);

  useEffect(() => {
    const photoRunning = jobs.filter((job) => job.kind === 'photo' && (job.status === 'running' || runningIds.current.has(job.id))).length;
    const videoRunning = jobs.filter((job) => job.kind === 'video' && (job.status === 'running' || runningIds.current.has(job.id))).length;
    const queuedJobs = jobs.filter((job) => job.status === 'queued' && !runningIds.current.has(job.id));

    // NAS 부하를 줄이기 위해 사진 처리와 동영상 변환은 동시에 실행하지 않습니다.
    // 사진 차례에는 최대 2장, 동영상 차례에는 1개만 처리합니다.
    if (videoRunning > 0) return;
    if (photoRunning > 0) {
      jobs
        .filter((job) => job.kind === 'photo' && job.status === 'queued' && !runningIds.current.has(job.id))
        .slice(0, Math.max(0, PHOTO_CONCURRENCY - photoRunning))
        .forEach((job) => { void runJob(job); });
      return;
    }

    const firstQueued = queuedJobs[0];
    if (!firstQueued) return;
    if (firstQueued.kind === 'video') {
      queuedJobs.filter((job) => job.kind === 'video').slice(0, VIDEO_CONCURRENCY).forEach((job) => { void runJob(job); });
      return;
    }
    queuedJobs.filter((job) => job.kind === 'photo').slice(0, PHOTO_CONCURRENCY).forEach((job) => { void runJob(job); });
  }, [jobs, runJob]);

  useEffect(() => {
    const batchIds = Array.from(new Set(jobs.map((job) => job.batchId)));
    batchIds.forEach((batchId) => {
      if (notifiedBatchIds.current.has(batchId)) return;
      const batchJobs = jobs.filter((job) => job.batchId === batchId);
      if (batchJobs.length > 0 && batchJobs.every(isTerminal)) {
        notifiedBatchIds.current.add(batchId);
        const summary = batchSummary(batchId, jobs);
        setCompletionQueue((current) => [...current, summary]);
        window.dispatchEvent(new CustomEvent('family-upload-complete', { detail: summary }));
      }
    });
  }, [jobs]);

  const enqueueUpload = useCallback((files: QueueUploadFile[], options: QueueUploadOptions): string => {
    if (files.length === 0) throw new Error('업로드할 파일이 없습니다.');
    if (options.mode === 'album' && !options.albumId) throw new Error('사진을 저장할 앨범을 선택해 주세요.');
    const batchId = makeId('batch');
    const newJobs: UploadQueueJob[] = files.map(({ file, kind }) => ({
      id: makeId('job'),
      batchId,
      file,
      kind,
      mode: options.mode,
      albumId: options.albumId,
      status: 'queued',
      progress: initialProgress(file),
    }));
    setJobs((current) => [...current, ...newJobs]);
    return batchId;
  }, []);

  const hasActiveUploads = jobs.some((job) => !isTerminal(job));
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (!hasActiveUploads) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasActiveUploads]);

  const dismissCompletion = useCallback(() => {
    const dismissed = completionQueue[0];
    if (dismissed) {
      setJobs((allJobs) => allJobs.filter((job) => job.batchId !== dismissed.batchId));
    }
    setCompletionQueue((current) => current.slice(1));
  }, [completionQueue]);

  const value = useMemo<UploadQueueContextValue>(() => ({
    enqueueUpload,
    queuedCount: jobs.filter((job) => job.status === 'queued').length,
    runningCount: jobs.filter((job) => job.status === 'running').length,
    hasActiveUploads,
  }), [enqueueUpload, hasActiveUploads, jobs]);

  return (
    <UploadQueueContext.Provider value={value}>
      {children}
      <QueueOverlay jobs={jobs} completion={completionQueue[0] ?? null} onDismissCompletion={dismissCompletion} />
    </UploadQueueContext.Provider>
  );
}

export function useUploadQueue(): UploadQueueContextValue {
  const context = useContext(UploadQueueContext);
  if (!context) throw new Error('useUploadQueue는 UploadQueueProvider 안에서 사용해야 합니다.');
  return context;
}
